<template>
  <BittsLoading :is-loading="!isReady">
    <div class="api-keys-tab">
      <ApiTabEmptyState
        v-if="!apiKeys.length"
        title="No API keys yet"
        description="Create a personal key to authenticate requests to the Crossbeam API. Usage, scopes, and rate limits will appear here once a key exists."
        create-cta-text="Create API key"
        :icon="['fak', 'lock']"
        :create-disabled-reason="createDisabledReason"
        @create="openCreate('keys')"
      />
      <template v-else>
        <ApiKeyUsageTiles v-if="limits" :limits="limits" />
        <ApiKeyCard
          v-for="apiKey in apiKeys"
          :key="apiKey.id"
          :api-key="apiKey"
          :can-revoke="canWrite"
          @revoke="keyPendingRevoke = apiKey"
        />
      </template>
      <BittsModal
        v-if="keyPendingRevoke"
        variant="confirm"
        :icon="['fas', 'trash-can']"
        icon-color="danger"
        title="Revoke API key?"
        :description="revokeDescription"
        primary-button-text="Revoke key"
        primary-button-type="danger"
        secondary-button-text="Cancel"
        :action-loading="isRevoking"
        @action="handleRevoke"
        @closed="keyPendingRevoke = null"
      />
    </div>
  </BittsLoading>
</template>

<script setup lang="ts">
import { BittsLoading, BittsModal } from '@crossbeam/bitts';

import { useHead } from '@unhead/vue';
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';

import { useApiKeysStore } from '@/stores';
import type { ApiKey } from '@/types/api_keys';

import ApiKeyCard from '../components/ApiKeyCard.vue';
import ApiKeyUsageTiles from '../components/ApiKeyUsageTiles.vue';
import ApiTabEmptyState from '../components/ApiTabEmptyState.vue';
import useApiKeyCreateGate from '../useApiKeyCreateGate';
import useApiRoutes from '../useApiRoutes';

useHead({ title: 'API Keys - Crossbeam' });

const { openCreate } = useApiRoutes();
const { canWrite, createDisabledReason } = useApiKeyCreateGate();
const apiKeysStore = useApiKeysStore();
const { apiKeys, limits, ready: isReady } = storeToRefs(apiKeysStore);

const keyPendingRevoke = ref<ApiKey | null>(null);
const isRevoking = ref(false);

const revokeDescription = computed(
  () =>
    `"${keyPendingRevoke.value?.name}" will stop working immediately for any integration using it. This cannot be undone.`,
);

async function handleRevoke() {
  if (!keyPendingRevoke.value) return;
  isRevoking.value = true;
  try {
    const hasRevoked = await apiKeysStore.revokeApiKey(
      keyPendingRevoke.value.id,
    );
    if (hasRevoked) keyPendingRevoke.value = null;
  } finally {
    isRevoking.value = false;
  }
}
</script>

<style scoped lang="pcss">
@reference "../../../css/theme.css";

.api-keys-tab {
  @apply flex flex-col gap-16;
}
</style>
